import React from 'react';
import { NavLink } from 'react-router-dom';
import { Row, Col, Form } from 'react-bootstrap';
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withCookies } from "react-cookie";

import CsLineIcons from '../../cs-line-icons';
import { login, preChallenge, sendChallenge, verifyChallenge } from "../../actions/auth";
import { settings as pageSettings } from '../../actions/page';
import { send as sendAlert } from '../../actions/alert';
import { isEmail } from '../../utils/regex';
import { only as numbersOnly } from "../../utils/number";

import LayoutFullPage from '../../layout/FullPage';
import Header from '../../layout/header';
import ConditionalButton from '../../layout/conditional-button';

export class Login extends React.Component {
    isCookieTrue = (name) => {
        const { cookies } = this.props;
        const value = cookies.get(name);
        return typeof value !== "undefined" && (value === true || value === "true");
    }

    maskEmail = (email) => {
        const s = String(email || "").trim();
        if (!s.includes("@")) return s ? "***" : "";
        const [user, domain] = s.split("@");
        const u = user.length <= 1 ? "*" : `${user[0]}***`;
        return `${u}@${domain}`;
    }

    maskPhone = (phone) => {
        const p = String(phone || "").replace(/\D/g, "");
        if (!p) return "";
        const last4 = p.slice(-4);
        return `(***) ***-${last4}`;
    }

    constructor(props) {
        super(props);

        // React state updates are asynchronous. Keep synchronous request locks so
        // a click/submit pair (or a fast double click) cannot replace the active
        // challenge before the user verifies it.
        this._credentialsInFlight = false;
        this._challengeSendInFlight = false;
        this._challengeVerifyInFlight = false;

        const seededEmail = this.email || "";

        this.state = {
            email: seededEmail,
            password: "",
            phone: "",
            code: "",

            step: 0, // 0=credentials, 1=2fa
            channel: "email", // selected method
            collect: "code", // "code" | "phone"

            token: null, // challenge token
            verifiedToken: null, // token passed to /login after verify
            sentToMasked: "",
            resendIn: 0,

            loading: false,
            remember: this.isCookieTrue("remember"),
            showMethodOptions: false, // only true after "Try another way"
        };
    }

    componentWillUnmount() {
        this.clearTimer();
    }

    clearTimer = () => {
        if (this._timer) clearInterval(this._timer);
        this._timer = null;
    }

    startCooldown = (seconds) => {
        this.clearTimer();
        const s = Number(seconds || 0);
        if (s <= 0) return;

        this.setState({ resendIn: s });
        this._timer = setInterval(() => {
            this.setState((prev) => {
                const next = Math.max(0, (prev.resendIn || 0) - 1);
                if (next === 0) this.clearTimer();
                return { resendIn: next };
            });
        }, 1000);
    }

    onSend = async () => {
        const { channel, email, phone, loading, addPhone } = this.state;
        if (loading || this._challengeSendInFlight) return;

        if (!isEmail(email)) {
            this.props.sendAlert("Please enter a valid email.", "error");
            return;
        }

        // If SMS selected but phone not collected yet, prompt for phone
        if (channel === "sms" && addPhone && numbersOnly(phone).length < 10) {
            this.setState({ collect: "phone" });
            return;
        }

        this._challengeSendInFlight = true;
        this.setState({ loading: true });
        try {
            const payload = {
                email,
                channel,
            };

            if (channel === "sms" && addPhone) payload.phone = numbersOnly(phone);

            const res = await this.props.sendChallenge(payload);

            if (!res?.success) {
                this.props.sendAlert("Unable to send verification code.", "error");
                return;
            }

            // Expect token from backend action response
            const token = res?.token || null;
            if (!token) {
                this.props.sendAlert("Challenge token missing from response.", "error");
                return;
            }

            const sentToMasked = res?.sentToMasked ||
                (channel === "email"
                    ? this.maskEmail(email)
                    : (addPhone ? this.maskPhone(phone) : "your phone on file"));

            this.setState({
                token,
                verifiedToken: null,
                sentToMasked,
                collect: "code",
                code: ""
            });

            this.startCooldown(res?.resendAfterSeconds || 30);
        } catch (e) {
            this.props.sendAlert(e?.message || "Unable to send verification code.", "error");
        } finally {
            this._challengeSendInFlight = false;
            this.setState({ loading: false });
        }
    };

    onChange = (e) => {
        this.setState({ [e.target.name]: e.target.value });
    }

    onEmailChange = (e) => {
        this.setState({
            email: e.target.value,
            token: null,
            verifiedToken: null
        });
    }

    onPhoneChange = (e) => {
        const raw = e?.target?.value ?? "";
        this.setState({
            phone: raw,
            token: null,
            verifiedToken: null
        });
    };

    onCodeChange = (e) => {
        this.setState({
            code: numbersOnly(e.target.value).slice(0, 6)
        });
    }

    onRememberChange = (e) => {
        this.onChange({
            target: {
                name: "remember",
                value: e.target.checked
            }
        })
    }

    // Step 0 -> Step 1 and auto-send challenge
    onCredentials = async (e) => {
        if (e?.preventDefault) e.preventDefault();

        if (this._credentialsInFlight) return;

        const { email, password } = this.state;
        if (!isEmail(email)) {
            this.props.sendAlert("Please enter a valid email.", "error");
            return;
        }
        if (!password) {
            this.props.sendAlert("Please enter your password.", "error");
            return;
        }

        this._credentialsInFlight = true;
        let setup = null;
        try {
            setup = await this.props.preChallenge({ email });
        } catch (err) {
            this.props.sendAlert(err?.message || "Unable to prepare verification.", "error");
            this._credentialsInFlight = false;
            return;
        }
        if (!setup?.success) {
            this._credentialsInFlight = false;
            this.props.sendAlert("Unable to prepare verification.", "error");
            return;
        }
        const active = !!setup?.active;
        const hasPhone = !!setup?.phone;
        const enabled = !!setup?.enabled;

        this.setState(
            {
                step: 1,
                channel: "email",          // default method
                collect: "code",
                token: null,
                verifiedToken: null,
                code: "",
                pending: active,
                addPhone: !hasPhone,
                showMethodOptions: false,   // hide methods until user asks,
                smsEnabled: enabled
            },
            async () => {
                this._credentialsInFlight = false;
                await this.onSend();       // auto-send once step 1 starts
            }
        );
    }

    onVerify = async () => {
        const { token, code, loading } = this.state;
        if (loading || this._challengeVerifyInFlight) return null;

        if (!token) {
            this.props.sendAlert("Missing verification token.", "error");
            return null;
        }

        const numericCode = numbersOnly(code);
        if (!numericCode) {
            this.props.sendAlert("Please enter the verification code.", "error");
            return null;
        }

        this._challengeVerifyInFlight = true;
        this.setState({ loading: true });
        try {
            const res = await this.props.verifyChallenge({ token, code: numericCode });
            if (!res?.success) {
                this.props.sendAlert("Invalid code.", "error");
                return null;
            }

            const verifiedToken = res?.token || token;
            this.setState({ verifiedToken });
            return verifiedToken;
        } catch (e) {
            this.props.sendAlert(e?.message || "Verification failed.", "error");
            return null;
        } finally {
            this._challengeVerifyInFlight = false;
            this.setState({ loading: false });
        }
    }

    onSubmit = async (e) => {
        if (e?.preventDefault) e.preventDefault();

        const { step, token, resendIn } = this.state;

        // If still on step 0, move to step 1 + auto send
        if (step === 0) {
            await this.onCredentials();
            return;
        }

        // Requirement: if token invalid and resend is eligible, Login click should sendChallenge
        if (!token) {
            if (resendIn === 0) {
                await this.onSend();
            } else {
                this.props.sendAlert(`Please wait ${resendIn}s before requesting another code.`, "error");
            }
            return;
        }

        // Verify code first
        const verifiedToken = await this.onVerify();
        if (!verifiedToken) return;

        // Final login
        const searchParams = new URLSearchParams(window.location.search);
        const nextUrl = searchParams.get("next");
        let found = false;

        if (!!this.props?.page?.meta?.forward && !!nextUrl) {
            for (let i = 0; i < this.props.page.meta.forward.length; i++) {
                const startsWith = this.props.page.meta.forward[i];
                found = found || nextUrl.startsWith(startsWith);
                if (found) break;
            }
        }

        const args = [];
        if (this.props?.page?.meta?.secure === "true") args.push(`s=1`);
        if (this.props?.page?.meta?.api) args.push(`a=${this.props?.page?.meta?.api}`);
        if (this.props?.page?.meta?.port) args.push(`p=${this.props?.page?.meta?.port}`);

        const __args = args.join("&");

        await this.props.login({
        instance: this.props?.page?.uuid,
            destination: this.props?.page?.destination,
            email: this.state.email,
            password: this.state.password,
            args: __args,
            url: found ? nextUrl : null,
            token: verifiedToken,
            remember: this.state.remember ? 1 : 0
        });
    };

    async componentDidMount() {
        await this.props.pageSettings();
    }

    get email() {
        return new URLSearchParams(window.location.search).get("e") || "";
    }

    get leftSide() {
        return (
            <div className="min-h-100 d-flex align-items-center">
                <div className="w-100 w-lg-75 w-xxl-50">
                    <div>
                        <div className="mb-5">
                            <h1 className="display-3 text-white">{this.line(2)}</h1>
                        </div>
                        <p className="h6 text-white lh-1-5 mb-5" dangerouslySetInnerHTML={{ __html: this.line(3) }} />
                        <div className="mb-5"></div>
                    </div>
                </div>
            </div>
        );
    }

    line(index) {
        return (this.props?.page?.meta?.lines?.length > index) ? this.props.page.meta.lines[index] : "";
    }

    caption(step) {
        switch (step) {
            case 1:
                return `Enter the code we sent to ${this.state.sentToMasked || "your destination"}.`;
            case 0:
            default:
                return "Please enter your credentials to login.";
        }
    }

    get rightSide() {
        const { loading, channel, email, password, step, resendIn, code, addPhone, smsEnabled, showMethodOptions } = this.state;

        const canContinue = isEmail(email) && !!password && !loading;
        const canLoginStep2 = numbersOnly(code).length === 6 && !loading;
        const canSendCode =
            !loading &&
            (resendIn <= 0) &&
            (
                channel !== "sms" ||
                (addPhone ? numbersOnly(this.state.phone).length >= 10 : true)
            );

        return (
            <div className="sw-lg-70 min-h-100 bg-foreground d-flex justify-content-center align-items-center shadow-deep py-5 full-page-content-right-border">
                <div className="sw-lg-50 px-5">
                    <div className="sh-11">
                        <NavLink to="/">
                            <div className="logo-default" style={{ backgroundImage: `url(${this.props.page?.meta?.images?.logos?.small})` }} />
                        </NavLink>
                    </div>

                    <div className="mb-5">
                        <h2 className="cta-1 mb-0 text-primary">{this.line(0)}</h2>
                        <h2 className="cta-1 text-primary">{this.line(1)}</h2>
                    </div>

                    <div className="mb-5">
                        <p className="h6">{this.caption(step)}</p>
                    </div>

                    {step === 0 ? (
                        <form onSubmit={this.onSubmit}>
                            <Row>
                                <Col sm="12">
                                    <div className="mb-3 filled form-group tooltip-end-top">
                                        <CsLineIcons icon="email" />
                                        <Form.Control
                                            className="form-control"
                                            name="email"
                                            value={email}
                                            onChange={this.onEmailChange}
                                            placeholder='Email'
                                            required
                                        />
                                    </div>
                                </Col>

                                <Col sm="12">
                                    <div className="mb-3 filled form-group tooltip-end-top">
                                        <CsLineIcons icon="lock-off" />
                                        <Form.Control
                                            className="form-control"
                                            type="password"
                                            placeholder=""
                                            name="password"
                                            value={password}
                                            onChange={this.onChange}
                                            required
                                        />
                                        <NavLink className="text-small position-absolute t-3 e-3" to={{ pathname: "/forgot", search: window.location.search }}>
                                            Forgot?
                                        </NavLink>
                                    </div>
                                </Col>

                                <Col sm="12" style={{ textAlign: "right" }}>
                                    <ConditionalButton
                                        type="submit"
                                        enabledVariant="outline-primary"
                                        display={true}
                                        enabled={canContinue}
                                    >
                                        Continue
                                    </ConditionalButton>
                                </Col>
                            </Row>
                        </form>
                    ) : step === 1 ? (
                        <form onSubmit={this.onSubmit}>
                            <Row>
                                <Col sm="12">
                                    <div className="mb-3 filled form-group tooltip-end-top">
                                        <CsLineIcons icon="check" />
                                        <Form.Control
                                            className="form-control"
                                            name="code"
                                            value={code}
                                            onChange={this.onCodeChange}
                                            placeholder='000000'
                                            inputMode='numeric'
                                            maxLength={6}
                                            minLength={6}
                                            required
                                        />
                                    </div>
                                </Col>

                                <Col sm="12" className="mt-2">
                                    {resendIn > 0 ? (
                                        <span className="text-muted text-small">
                                            You can resend in {resendIn}s.
                                        </span>
                                    ) : (
                                        <div className="d-flex align-items-center gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-link p-0"
                                                onClick={this.onSend}
                                                disabled={!canSendCode}
                                            >
                                                Resend now
                                            </button>
                                            {smsEnabled && !showMethodOptions && (
                                                <>
                                                    <span className="text-muted">or</span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0"
                                                        disabled={loading}
                                                        onClick={() =>
                                                            this.setState({
                                                                showMethodOptions: true,
                                                                token: null,
                                                                verifiedToken: null,
                                                                code: "",
                                                                ...(this.state.channel === "email"
                                                                    ? { channel: "sms", collect: "phone" }
                                                                    : { channel: "email", collect: "code" })
                                                            })
                                                        }
                                                    >
                                                        Try another way
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </Col>

                                {resendIn === 0 && smsEnabled && showMethodOptions && (
                                    <>
                                        <Col sm="12" className="mt-2">
                                            <div className="mb-3 filled form-group tooltip-end-top">
                                                <Form.Check
                                                    type="radio"
                                                    id="mfa-email"
                                                    name="mfa-method"
                                                    label={`Email (${this.maskEmail(email) || "on file"})`}
                                                    checked={channel === "email"}
                                                    onChange={() =>
                                                        this.setState({
                                                            channel: "email",
                                                            collect: "code",
                                                            token: null,
                                                            verifiedToken: null,
                                                            code: ""
                                                        })
                                                    }
                                                    disabled={loading}
                                                />
                                            </div>
                                        </Col>

                                        
                                        <Col sm="12">
                                            <div className="mb-2 filled form-group tooltip-end-top">
                                                <Form.Check
                                                    type="radio"
                                                    id="mfa-sms"
                                                    name="mfa-method"
                                                    label="SMS (text message)"
                                                    checked={channel === "sms"}
                                                    onChange={() =>
                                                        this.setState({
                                                            channel: "sms",
                                                            collect: "phone",
                                                            token: null,
                                                            verifiedToken: null,
                                                            code: ""
                                                        })
                                                    }
                                                    disabled={loading}
                                                />
                                            </div>

                                            {channel === "sms" && (
                                                <div className="mb-3 ms-4">
                                                    {addPhone ? (
                                                        <Form.Control
                                                            className="form-control"
                                                            name="phone"
                                                            value={this.state.phone}
                                                            onChange={this.onPhoneChange}
                                                            placeholder="(555) 555-1234"
                                                        />
                                                    ) : (
                                                        <div className="text-muted text-small">
                                                            We’ll text a code to your phone on file.
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                        </Col>
                                    </>
                                )}

                                <Col sm="12" className="mt-2">
                                    <div className="d-flex justify-content-end align-items-center gap-2">
                                        {channel === "sms" && (
                                            <ConditionalButton
                                                enabledVariant="outline-primary"
                                                display={true}
                                                enabled={canSendCode}
                                                onEnabledClick={this.onSend}
                                            >
                                                Send Code
                                            </ConditionalButton>
                                        )}
                                        <ConditionalButton
                                            enabledVariant="outline-primary"
                                            disabledVariant="outline-secondary"
                                            display
                                            enabled={canLoginStep2}
                                            type="submit"
                                        >
                                            Login
                                        </ConditionalButton>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    ) : null}
                </div>
            </div>
        );
    }

    render() {
        if (!this.props.page) return <></>;
        if (!this.props.page.meta) return <></>;

        return (
            <>
                <Header title='Login' description='Login' />
                <LayoutFullPage left={this.leftSide} right={this.rightSide} background={this.props.page?.meta?.images?.background} />
            </>
        );
    }
};

Login.propTypes = {
    login: PropTypes.func.isRequired,
    preChallenge: PropTypes.func.isRequired,
    sendChallenge: PropTypes.func.isRequired,
    verifyChallenge: PropTypes.func.isRequired,
    sendAlert: PropTypes.func.isRequired,
    pageSettings: PropTypes.func.isRequired,
    authenticated: PropTypes.bool,
    page: PropTypes.object
};

const mapStateToProps = (state) => ({
    authenticated: state.auth.authenticated,
    page: state.page
});

export default withCookies(
    connect(mapStateToProps, {
        login,
        preChallenge,
        sendChallenge,
        verifyChallenge,
        sendAlert,
        pageSettings
    })(Login)
);
